import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2, MessageSquarePlus } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useCreateChat } from '@/hooks/useChat'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

interface SearchUser {
  id: string
  username: string
  gravatarHash?: string
}

interface NewChatDialogProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Dialog for creating a new chat with another user.
 *
 * Flow:
 * 1. User searches for another user by username
 * 2. User selects a result
 * 3. Creates a new chat and adds the selected user as member
 * 4. Navigates to the new chat
 */
export function NewChatDialog({ isOpen, onClose }: NewChatDialogProps) {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<SearchUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null)
  const createChat = useCreateChat()

  // Debounced search
  const handleSearch = useCallback(async (term: string) => {
    setSearchTerm(term)
    setSelectedUser(null)

    if (term.length < 2) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const results = await api.get<SearchUser[]>(`/api/search/users/${encodeURIComponent(term)}`)
      setSearchResults(results || [])
    } catch {
      // Search failed - show empty results (no users found)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  // Handle user selection
  const handleSelectUser = (user: SearchUser) => {
    setSelectedUser(user)
    setSearchTerm(user.username)
    setSearchResults([])
  }

  // Create chat and navigate
  const handleCreateChat = async () => {
    if (!selectedUser) return

    try {
      const chatId = await createChat.mutateAsync(selectedUser.id)
      onClose()
      navigate(`/chat/${chatId}`)
    } catch {
      // Error is handled by React Query's isError state
      // The button will show the error via createChat.isError
    }
  }

  // Reset state when dialog closes
  const handleClose = () => {
    setSearchTerm('')
    setSearchResults([])
    setSelectedUser(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New Chat">
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search for a user..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className={cn(
              'w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4',
              'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            )}
            autoFocus
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
          )}
        </div>

        {/* Search results */}
        {searchResults.length > 0 && (
          <ul className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
            {searchResults.map((user) => (
              <li key={user.id}>
                <button
                  onClick={() => handleSelectUser(user)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2 text-left',
                    'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                    selectedUser?.id === user.id && 'bg-primary/10'
                  )}
                >
                  {/* Avatar */}
                  <img
                    src={`https://www.gravatar.com/avatar/${user.gravatarHash || ''}?s=32&d=identicon`}
                    alt=""
                    className="h-8 w-8 rounded-full"
                  />
                  <span className="font-medium text-text-dark">{user.username}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* No results message */}
        {searchTerm.length >= 2 && !isSearching && searchResults.length === 0 && !selectedUser && (
          <p className="text-center text-sm text-gray-500">No users found</p>
        )}

        {/* Selected user indicator */}
        {selectedUser && (
          <div className="flex items-center gap-3 rounded-lg bg-primary/10 p-3">
            <img
              src={`https://www.gravatar.com/avatar/${selectedUser.gravatarHash || ''}?s=32&d=identicon`}
              alt=""
              className="h-8 w-8 rounded-full"
            />
            <span className="font-medium text-text-dark">{selectedUser.username}</span>
          </div>
        )}

        {/* Error message */}
        {createChat.isError && (
          <p className="text-center text-sm text-red-500">
            Failed to create chat. Please try again.
          </p>
        )}

        {/* Create button */}
        <button
          onClick={handleCreateChat}
          disabled={!selectedUser || createChat.isPending}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-lg py-2.5 font-medium text-white',
            'bg-primary hover:bg-primary-dark',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {createChat.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <MessageSquarePlus className="h-5 w-5" />
              Start Chat
            </>
          )}
        </button>
      </div>
    </Modal>
  )
}
